import React, { useState } from 'react';
import { FiChevronDown, FiChevronUp, FiCopy } from 'react-icons/fi';
import { pillClass, formatDate, formatArs } from './adminTheme';
import { useThemeClasses } from '../../hooks/useThemeClasses';

function paymentTone(status) {
  const s = String(status || '').toLowerCase();
  if (['authorized', 'active', 'approved'].includes(s)) return 'success';
  if (s === 'pending') return 'warning';
  if (['canceled', 'cancelled', 'rejected'].includes(s)) return 'danger';
  return 'neutral';
}

function subscriptionSummaryTone(status) {
  const s = String(status || '').toLowerCase();
  if (['active', 'trialing', 'authorized'].includes(s)) return 'success';
  if (['pending_payment', 'past_due', 'grace_readonly'].includes(s)) return 'warning';
  if (['suspended', 'canceled', 'cancelled', 'purged'].includes(s)) return 'danger';
  return 'neutral';
}

function SummaryRow({ label, children }) {
  const tc = useThemeClasses();
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between gap-y-1">
      <span className={`text-xs ${tc.textSec}`}>{label}</span>
      <span className={`text-xs font-medium ${tc.textPri}`}>{children}</span>
    </div>
  );
}

export default function AdminUserBillingPanel({ insights, loading }) {
  const tc = useThemeClasses();
  const [technicalOpen, setTechnicalOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  if (loading) {
    return (
      <div className={`rounded-xl border p-3 ${tc.modoOscuro ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-gray-50'}`}>
        <p className={`text-xs ${tc.textMuted}`}>Cargando facturación…</p>
      </div>
    );
  }

  if (!insights?.summary) {
    return (
      <div className={`rounded-xl border p-3 ${tc.modoOscuro ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-gray-50'}`}>
        <p className={`text-xs ${tc.textMuted}`}>No hay datos de facturación para este usuario.</p>
      </div>
    );
  }

  const { summary, paymentHistory, stats, billingContext } = insights;
  const renewalLabel = formatDate(summary.renewalAt, 'Sin fecha cargada');
  const graceLabel = summary.graceEndsAt ? formatDate(summary.graceEndsAt) : null;
  const preapprovalId = summary.currentPreapprovalId;

  const copyPreapproval = async () => {
    if (!preapprovalId || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(preapprovalId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className={`rounded-xl border p-3 space-y-3 ${tc.modoOscuro ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-gray-50'}`}>
      <div>
        <p className={`text-xs font-semibold uppercase tracking-wide ${tc.textSec}`}>Facturación</p>
        {billingContext?.ownerEmail && (
          <p className={`text-[11px] mt-1 ${tc.textMuted}`}>
            Cobro de la empresa {billingContext.companyName || ''} · titular {billingContext.ownerEmail}
          </p>
        )}
      </div>

      <div className={`rounded-lg border p-2.5 space-y-2 ${tc.modoOscuro ? 'border-gray-800 bg-black/30' : 'border-gray-200 bg-white'}`}>
        <SummaryRow label="Estado">
          <span className={pillClass(tc.modoOscuro, subscriptionSummaryTone(summary.status))}>
            {summary.statusLabel}
          </span>
        </SummaryRow>
        <SummaryRow label="Próxima renovación">{renewalLabel}</SummaryRow>
        {graceLabel && (
          <SummaryRow label="Fin de gracia">{graceLabel}</SummaryRow>
        )}
        <SummaryRow label="Renovación automática">
          {summary.autoRenew ? 'Sí (Mercado Pago)' : 'No'}
        </SummaryRow>
      </div>

      {paymentHistory?.length > 0 ? (
        <div>
          <p className={`text-[11px] font-medium uppercase tracking-wide mb-2 ${tc.textSec}`}>
            Cobros registrados
          </p>
          <ul className="space-y-2">
            {paymentHistory.map((pay) => (
              <li
                key={pay.id}
                className={`rounded-lg border px-2.5 py-2 ${tc.modoOscuro ? 'border-gray-800 bg-black/20' : 'border-gray-200 bg-white'}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className={`text-xs font-medium ${tc.textPri}`}>{formatArs(pay.amount)}</span>
                  <span className={pillClass(tc.modoOscuro, paymentTone(pay.status))}>
                    {pay.statusLabel}
                  </span>
                </div>
                <p className={`text-[11px] mt-1 ${tc.textMuted}`}>
                  {formatDate(pay.createdAt)}
                  {pay.isCurrent ? ' · Suscripción actual' : ''}
                </p>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className={`text-xs ${tc.textMuted}`}>Sin cobros exitosos registrados en Contsuite.</p>
      )}

      {stats?.abandonedCheckoutCount > 0 && (
        <p className={`text-[11px] ${tc.textMuted}`}>
          {stats.abandonedCheckoutCount} intento(s) de checkout sin completar (registro o upgrade). No se muestran para no saturar la vista.
        </p>
      )}

      {preapprovalId && (
        <div>
          <button
            type="button"
            onClick={() => setTechnicalOpen((v) => !v)}
            className={`flex items-center gap-1 text-[11px] font-medium ${tc.textSec} hover:underline`}
          >
            {technicalOpen ? <FiChevronUp className="w-3 h-3" /> : <FiChevronDown className="w-3 h-3" />}
            Datos técnicos (soporte MP)
          </button>
          {technicalOpen && (
            <div className={`mt-2 rounded-lg border p-2 ${tc.modoOscuro ? 'border-gray-800' : 'border-gray-200'}`}>
              <p className={`text-[10px] uppercase tracking-wide ${tc.textMuted}`}>ID suscripción MP</p>
              <div className="flex items-start gap-2 mt-1">
                <code className={`text-[11px] break-all flex-1 ${tc.textPri}`}>{preapprovalId}</code>
                <button
                  type="button"
                  onClick={copyPreapproval}
                  className={`shrink-0 p-1 rounded border ${tc.btnOutline}`}
                  title="Copiar ID"
                >
                  <FiCopy className="w-3.5 h-3.5" />
                </button>
              </div>
              {copied && <p className={`text-[10px] mt-1 text-green-600 dark:text-green-400`}>Copiado</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
